/**
 * The red lantern's notes (ใบโคม, docs/archive/QINGGONG_DROPS_RED_LANTERN.md Part B): four quest templates, and what each
 * kind of giver pays. Data only; rules: systems/redLantern.ts.
 */
import type { NpcRole } from './npcActs';
import type { LanternQuestKind } from './redLantern';

export const LANTERN_QUESTS = {
  /** A quest ends this many seconds after it is taken. */
  expire: 600,
  /** Roles that give a note. */
  givers: ['vendor', 'fisher', 'smith', 'elder', 'innkeeper', 'farmer', 'villager', 'scholar'] as readonly NpcRole[],
  /** Standing the giver's regard rises by when it is done (systems/npcMemory.ts). */
  regard: 8,
  /** Every role adds this many ตำลึง (min-max) to its item. */
  gold: [20, 80] as const,
  deliver: { minDist: 120, maxDist: 600, carry: 240, near: 3 },
  find: { minDist: 60, maxDist: 200, circle: 25, near: 2.5 },
  help: { good: 'herb-leaf', n: 5, near: 3 },
  escort: { minDist: 150, maxDist: 300, near: 3, ambushers: 2, monster: 'bandit' },
  /** Help asks for something else by the giver's role (default: `help`); the note keeps good and n. */
  helpBy: {
    fisher: { good: 'carp', n: 3, text: 'ใบโคม: {giver} ต้องการปลาไน {n} ตัว' },
    farmer: { good: 'rice', n: 4, text: 'ใบโคม: {giver} ต้องการข้าวเปลือก {n} ถุง' },
    vendor: { good: 'flour', n: 3, text: 'ใบโคม: {giver} ต้องการแป้งข้าว {n} ถุง' },
    innkeeper: { good: 'cabbage', n: 4, text: 'ใบโคม: {giver} ต้องการผักกาด {n} หัว' },
  } as Readonly<Partial<Record<NpcRole, { good: string; n: number; text: string }>>>,
  /** Lines shown per kind (Thai, player-facing): {giver} {receiver} {n}. */
  text: {
    deliver: 'ใบโคม: {giver} ฝากพัสดุไปให้ {receiver} ภายใน 4 นาที',
    find: 'ใบโคม: {giver} ทำของหายใกล้วงกลมบนแผนที่ ช่วยตามหาที',
    help: 'ใบโคม: {giver} ต้องการสมุนไพร {n} ใบ',
    escort: 'ใบโคม: {giver} ขอให้พาไปส่งยังจุดหมาย ระวังโจรดักทาง',
  } as Readonly<Record<LanternQuestKind, string>>,
} as const;

/** What a giver pays besides ตำลึง, by role: goods (id, n) and a chance of an extra. */
export const LANTERN_PAYS: Readonly<Partial<Record<NpcRole, { good: string; n: number; extra?: { good: string; chance: number } }>>> = {
  fisher: { good: 'carp', n: 3, extra: { good: 'gem-pearl-1', chance: 0.25 } },
  smith: { good: 'hone-dust', n: 2 },
  farmer: { good: 'rice', n: 4 },
  vendor: { good: 'flour', n: 3 },
  innkeeper: { good: 'cabbage', n: 3 },
  elder: { good: 'indigo-dye', n: 1 },
  scholar: { good: 'indigo-dye', n: 1 },
  villager: { good: 'rice', n: 2 },
};

/** In a region with no townsfolk near, two villagers wait at the nearest camps' edges (systems/redLantern.ts). */
export const LANTERN_CAMP_GIVERS = [
  { name: 'พรานป่าชายค่าย', title: 'ชาวบ้านชายป่า', sprite: 'fisherOld' },
  { name: 'หญิงเก็บยาชายค่าย', title: 'ชาวบ้านชายป่า', sprite: 'teaSeller' },
] as const;
